import { useEffect, useState, type FormEvent } from 'react'
import { api, errorMessage } from '../api/client'
import type { CustomerTotals } from '../api/types'
import { Amt, Card, Field, Note, NumberInput, PageHead, TotalBox } from '../components/ui'
import { PersonPicker } from '../components/PersonPicker'
import { useAuth } from '../auth/AuthContext'
import { useApi } from '../hooks/useApi'
import { useCustomerPicker } from '../hooks/usePickers'

interface OpeningBalanceView {
  customerId: string
  oldSold: string
  oldPaid: string
}

/**
 * The one-time figures for people who already owed money before this book
 * existed.
 *
 * Kept as a pair - what they had bought and what they had paid - rather than
 * one net balance, so the ledger can show where the old number came from, and
 * so a later correction to one half does not look like a payment that never
 * happened.
 */
export function OpeningBalances() {
  const { isOwner } = useAuth()
  const customers = useCustomerPicker()
  const [customerId, setCustomerId] = useState<string | null>(null)
  const opening = useApi<OpeningBalanceView>(
    customerId ? `/customers/${customerId}/opening-balance` : null,
  )

  const [oldSold, setOldSold] = useState('')
  const [oldPaid, setOldPaid] = useState('')
  const [saved, setSaved] = useState<CustomerTotals | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setOldSold(opening.data?.oldSold ?? '')
    setOldPaid(opening.data?.oldPaid ?? '')
    setSaved(null)
  }, [opening.data])

  async function submit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    try {
      const response = await api.put<CustomerTotals>(
        `/customers/${customerId}/opening-balance`,
        { oldSold: oldSold || '0', oldPaid: oldPaid || '0' },
      )
      setSaved(response.data)
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  if (!isOwner) {
    return (
      <>
        <PageHead title="پرانا حساب" />
        <Note kind="warn">یہ صفحہ صرف مالک کھول سکتے ہیں۔</Note>
      </>
    )
  }

  return (
    <>
      <PageHead
        title="پرانا حساب"
        hint="جو گاہک اِس ایپ سے پہلے کے بقایا دار ہیں، اُن کی ایک بار کی انٹری۔"
      />

      {error && <Note kind="error">{error}</Note>}

      <Card>
        <form onSubmit={submit}>
          <div className="row">
            <PersonPicker
              label="گاہک"
              value={customerId}
              onChange={setCustomerId}
              people={customers.people}
              recentIds={customers.recentIds}
              loading={customers.loading}
            />

            <Field label="پرانی فروخت">
              <NumberInput value={oldSold} onChange={setOldSold} placeholder="0" />
            </Field>

            <Field label="پرانی ادائیگی">
              <NumberInput value={oldPaid} onChange={setOldPaid} placeholder="0" />
            </Field>

            <div className="field">
              <label>&nbsp;</label>
              <button className="btn" disabled={!customerId}>
                محفوظ کریں
              </button>
            </div>
          </div>
        </form>
      </Card>

      {saved && (
        <div className="totals">
          <TotalBox label="کل فروخت" value={<Amt value={saved.totalSale} />} />
          <TotalBox label="کل ادائیگی" value={<Amt value={saved.totalPaid} />} />
          <TotalBox label="بقایا" value={<Amt value={saved.balance} tone="balance" />} accent />
        </div>
      )}
    </>
  )
}
